// Copyright ©️ 2026 eVolve MEP, LLC

import { createContext, type ReactNode, useCallback, useMemo, useState } from 'react';

import { useGeneralContext } from 'helpers/useGeneralContext';
import type { CartBend } from 'modules/Shop/EvolveFabrication/BendCreator/Cart/useBendCart';
import type { BendCreatorFormValues } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorFormContext';
import type { LastValidationState } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorValidationContext';
import { type BendCreatorStepIndex, nextStepOf } from 'modules/Shop/EvolveFabrication/BendCreator/Nav/bendCreatorSteps';

/** Everything about the bend being built, saved while the panel edits a bend from the list instead. */
export type BuilderSnapshot = {
  values: BendCreatorFormValues;
  lastValidation: LastValidationState | null;
  finishedSteps: ReadonlySet<BendCreatorStepIndex>;
  isAutoNamed: boolean;
};

/** The panel is editing a bend from the list. */
export type EditSession = {
  /** The bend as it was before editing began, for Cancel. */
  bendSnapshot: CartBend;
  /** The bend being built, restored when editing ends. */
  builderSnapshot: BuilderSnapshot;
};

export type BendBuilderContextType = {
  /** `root` shows the list of steps; `step` shows one step, pushed over the list. */
  nav: 'root' | 'step';
  /** The step shown while `nav` is `step`. */
  currentStep: BendCreatorStepIndex;
  /** Steps the user has finished with Next. A step also needs its values to show as done; see `useStepStatuses`. */
  finishedSteps: ReadonlySet<BendCreatorStepIndex>;
  setFinishedSteps: (finishedSteps: ReadonlySet<BendCreatorStepIndex>) => void;
  /** True while the part name was set automatically, so a bend type change may rename it. */
  isAutoNamed: boolean;
  setIsAutoNamed: (isAutoNamed: boolean) => void;
  /** Set while the panel edits a bend from the list. Managed by `useCopyEditing`. */
  editSession: EditSession | null;
  setEditSession: (editSession: EditSession | null) => void;
  /** Pushes a step over the list. */
  openStep: (step: BendCreatorStepIndex) => void;
  /** Pops back to the list of steps. */
  openRoot: () => void;
  /** Marks the current step finished and pushes the next one. */
  goToNextStep: () => void;
  /** Returns to a fresh bend: the list of steps, with no step finished. */
  resetBuilder: () => void;
};

const BendBuilderContext = createContext<BendBuilderContextType | undefined>(undefined);

export const BendBuilderProvider = ({ children }: { children: ReactNode }) => {
  const [nav, setNav] = useState<BendBuilderContextType['nav']>('root');
  const [currentStep, setCurrentStep] = useState<BendCreatorStepIndex>(0);
  const [finishedSteps, setFinishedSteps] = useState<ReadonlySet<BendCreatorStepIndex>>(new Set());
  const [isAutoNamed, setIsAutoNamed] = useState(true);
  const [editSession, setEditSession] = useState<EditSession | null>(null);

  const openStep = useCallback((step: BendCreatorStepIndex) => {
    setCurrentStep(step);
    setNav('step');
  }, []);

  const openRoot = useCallback(() => setNav('root'), []);

  const goToNextStep = useCallback(() => {
    setFinishedSteps((finished) => new Set(finished).add(currentStep));
    const nextStep = nextStepOf[currentStep];
    if (nextStep !== null) setCurrentStep(nextStep);
  }, [currentStep]);

  const resetBuilder = useCallback(() => {
    setFinishedSteps(new Set());
    setIsAutoNamed(true);
    setNav('root');
  }, []);

  const value = useMemo(
    () => ({
      nav,
      currentStep,
      finishedSteps,
      setFinishedSteps,
      isAutoNamed,
      setIsAutoNamed,
      editSession,
      setEditSession,
      openStep,
      openRoot,
      goToNextStep,
      resetBuilder,
    }),
    [nav, currentStep, finishedSteps, isAutoNamed, editSession, openStep, openRoot, goToNextStep, resetBuilder],
  );

  return <BendBuilderContext value={value}>{children}</BendBuilderContext>;
};

export const useBendBuilder = () => useGeneralContext(BendBuilderContext, 'BendBuilderContext');
